import { Component, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, Subscription } from 'rxjs';
import { F4562Service } from './f-4562.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { safeEvalFormula } from '../../../shared/_utils/safe-eval-formula';

import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

interface F4562DataNode {
  ROW_TYPE?: string;
  Line_data?: F4562DataNode[];
  LINE_ATTRIB_KEY?: string | number | null;
  ATTRIB_SHORT_CODE?: string | null;
  ATTRIB_DESCRIPTION?: unknown;
  ATTRIB_VALUE?: unknown;
  COLSPAN?: number | string | null;
  ATTRIB_ORDER?: number | string | null;
  LINE_NO?: string;
  IS_EDITABLE?: string;
}

@Component({
    selector: 'gtw-f-4562-ce',
    templateUrl: './f-4562.component.html',
    styleUrls: ['./f-4562.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F4562Component implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};
  
  filterParams: any = {};
  
  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;

  // Row data from AngularJS
  rowData: any = {};

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Input Details";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;

  // Component properties from AngularJS controller
  f4562Header: any = {};
  f4562List: any[] = [];
  f4562OriginalData: any[] = [];
  f4562FormData: any[] = [];
  f4562ColumnHeader: any[] = [];
  f4562FinalData: any[] = [];
  f4562LHData: any = {};
  
  scenario: string = '';
  glbl_issue_id: string = '';
  local_issue_id: string = '';
  bShowSaveButton: boolean = true;
  cur_scenario_type: string = '';
  
  f4562FACSHeader: any = {};
  f4562FACSList: any[] = [];
  f4562FACSFormData: any[] = [];
  f4562FACSLHData: any = {};
  
  LH_index: number = 0;
  unAllocatedAmt: number = 0;
  
  // Dropdown options
  convensions: string[] = ["HY", "MM", "MQ", "S/L"];
  methods: string[] = ["150 DB", "200 DB", "DB", "S/L"];
  recoveryPeriod: string[] = ["3 Yrs", "5 Yrs", "7 Yrs", "10 Yrs", "15 Yrs", "20 Yrs", "25 Yrs", "27.5 Yrs", "39 Yrs", "50 Yrs"];
  
  isValidScenario: boolean = false;
  isExamScenario: boolean = false;
  
  userSettings: any;
  logged_in_user: string = '';
  
  // Tab configuration
  tabs = [
    { id: 1, name: "Input Details", active: true },
    { id: 2, name: "FACS Details", active: true }
  ];
  activeTab = 1;
  selectedTabName: string = 'Input Details';

  // Entity data properties
  entityList: any[] = [];

  // Data loading optimization
  private dataLoadingStates = {
    entityData: false,
    formData: false,
    facsData: false
  };

  constructor(
    private f4562Service: F4562Service,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    if (this.rowData && Object.keys(this.rowData).length > 0) {
      this.setupEntityData();
      this.showFormLoader = false;
      this.init();
    } else {
      this.showFormLoader = true;
    }
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        if (this.rowData && Object.keys(this.rowData).length > 0) {
          this.setupEntityData();
          this.showFormLoader = false;
          this.init();
        }
        this.cdr.markForCheck();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        if (this.rowData && Object.keys(this.rowData).length > 0) {
          this.setupEntityData();
          this.showFormLoader = false;
          this.init();
        }
        this.cdr.markForCheck();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal) as GtwElementsBaseUrlsModel;
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow) ? extractObject(rawRow[0]) : extractObject(rawRow);
    this.appService = { USER_SETTINGS: header.userSettingsData || {}, SERVER_CONFIG: header.serverConfigData || {} };
    this.cdr.markForCheck();
  }

  init() {
    try {
      if (!this.globalService || !this.GENERAL_CONFIG || !this.rowData || Object.keys(this.rowData).length === 0) {
        return;
      }

      this.userSettings = this.appService.USER_SETTINGS;
      this.logged_in_user = this.userSettings?.user?.sso_id || '';
      
      this.scenario = this.filterParams.scenario || this.globalService?.globalParams?.scenario || '';
      this.glbl_issue_id = this.globalService?.globalParams?.issue_id || '';
      
      // Check exam scenario
      this.checkExamScenario();
      
      if (this.rowData && Object.keys(this.rowData).length > 0) {
        this.setupEntityData();
        this.loadFormData();
      }
    } catch (e) {
      console.error('Error in F4562 init:', e);
      this.showFormLoader = false;
    }
  }

  private setupEntityData() {
    // Use REAL data from rowData - NO TEST_ values!
    this.entityList = [
      {key: "Tax Year", value: this.rowData.TAX_YEAR || ''},
      {key: "LEID", value: this.rowData.LEID || ''},
      {key: "LE Name", value: this.rowData.LE_NAME || ''}
    ];
    
    this.dataLoadingStates.entityData = true;
    this.checkAllDataLoaded();
  }

  checkExamScenario() {
    // Check if scenario type is EXAM
    const scenarioTypeCode = this.globalService?.inputs?.tax_year?.selected?.scenario_type_code;
    this.isExamScenario = scenarioTypeCode === 'EXAM' && this.checkExamScenarioFilter();
  }

  checkExamScenarioFilter(): boolean {
    // This would need to check filters - simplified for now
    // In real implementation, would check filterParams
    return false;
  }

  checkAllDataLoaded() {
    const allLoaded = Object.values(this.dataLoadingStates).every(state => state);
    if (allLoaded) {
      // Initialize select dropdowns after all data is loaded
      this.initializeSelectDropdowns(this.f4562FormData);
      this.initializeSelectDropdowns(this.f4562FACSFormData);
      
      this.showFormLoader = false;
      this.cdr.detectChanges();
    }
  }

  loadFormData() {
    // Load both Input Details and FACS Details in parallel
    const inputDetails$ = this.f4562Service.fetchF4562Details(
      this.GENERAL_CONFIG.base_url,
      this.rowData,
      this.logged_in_user,
      this.scenario,
      this.globalService
    );
    
    const facsDetails$ = this.f4562Service.fetchF4562FACSDetails(
      this.GENERAL_CONFIG.base_url,
      this.rowData,
      this.logged_in_user,
      this.scenario,
      this.globalService
    );

    forkJoin([inputDetails$, facsDetails$]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ([inputData, facsData]: [any, any]) => {
        if (inputData && inputData.jsonObject) {
          this.processFormData(inputData);
        }
        if (facsData && facsData.jsonObject) {
          this.processFACSData(facsData);
        }
        this.dataLoadingStates.formData = true;
        this.dataLoadingStates.facsData = true;
        this.checkAllDataLoaded();
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        console.error('Error loading form data:', error);
        this.showFormLoader = false;
        this._snackBar.open('Error loading form data. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.markForCheck();
      }
    });
  }

  private processFormData(data: any) {
    this.f4562Header = data.jsonObject[0];
    this.f4562List = data.jsonObject.slice(1);
    
    // Check valid scenario
    for (let i = 0; i < this.f4562List.length; i++) {
      if (!this.isValidScenario && this.f4562List[i].T_SCENARIO_TYPE_CODE) {
        this.isValidScenario = this.checkEditScenario(
          this.f4562List[i].T_SCENARIO_TYPE_CODE,
          this.f4562List[i].T_SCENARIO,
          this.f4562List[i].ISSUE_KEY
        );
      }
    }
    
    // Process data structure exactly like AngularJS
    const o = this.f4562List.reduce((a: any, b: any) => {
      a[b.LINE_ORDER] = a[b.LINE_ORDER] || [];
      a[b.LINE_ORDER].push(b);
      return a;
    }, {});
    
    const a = Object.keys(o).map((k: string) => {
      return {
        ROW_TYPE: o[k][0].ROW_TYPE,
        DELETE_ROWS: [],
        Line_data: this.dedupeLhCells(o[k])
      };
    });
    
    this.f4562FormData = a;
    
    // Process LH data
    this.LH_index = this.f4562FormData.findIndex((x: any) => x.ROW_TYPE == "LH");
    
    const n = this.f4562FormData.reduce((a: any, b: any) => {
      a[b.ROW_TYPE] = a[b.ROW_TYPE] || [];
      a[b.ROW_TYPE].push(b);
      return a;
    }, {});
    
    const b = Object.keys(n).map((k: string) => {
      if (k == 'LH') {
        return {ROW_TYPE: n[k][0].ROW_TYPE, DELETE_ROWS: [], Line_data: n[k]};
      }
      return undefined;
    }).filter((item: any) => item !== undefined);
    
    // Remove LH from main array
    for (let i = this.f4562FormData.length - 1; i >= 0; i--) {
      if (this.f4562FormData[i].ROW_TYPE == 'LH') {
        this.f4562FormData.splice(i, 1);
      }
    }
    
    // Process LH data structure
    for (let g = 0; g < b.length; g++) {
      const bgItem = b[g];
      if (bgItem != undefined && bgItem.Line_data) {
        for (let h = 0; h < bgItem.Line_data.length; h++) {
          const lineDataItem = bgItem.Line_data[h];
          if (lineDataItem && lineDataItem.Line_data && lineDataItem.Line_data[0] && lineDataItem.Line_data[0].OCCURRENCE != null) {
            const z = lineDataItem.Line_data.reduce((a: any, b: any) => {
              a[b.OCCURRENCE] = a[b.OCCURRENCE] || [];
              a[b.OCCURRENCE].push(b);
              return a;
            }, {});
            
            const y = Object.keys(z).map((k: string) => {
              return {ROW_TYPE: z[k][0].ROW_TYPE, DELETE_ROWS: [], Line_data: z[k]};
            });
            
            if (y.length > 0) {
              bgItem.Line_data.splice(h, 1, y[0]);
              if (y.length > 1) {
                for (let a = 1; a < y.length; a++) {
                  bgItem.Line_data.splice(h + a, 0, y[a]);
                }
              }
            }
          }
        }
        this.f4562LHData = bgItem;
      }
    }
    
    // Insert LH data at original index
    if (this.f4562LHData && Object.keys(this.f4562LHData).length > 0) {
      this.f4562FormData.splice(this.LH_index, 0, this.f4562LHData);
    }
    
    // Process CH rows
    for (let i = 0; i < this.f4562FormData.length; i++) {
      if (this.f4562FormData[i].ROW_TYPE == 'CH') {
        if (this.f4562FormData[i].Line_data[0] && this.f4562FormData[i].Line_data[0].OCCURRENCE != null) {
          const z = this.f4562FormData[i].Line_data.reduce((a: any, b: any) => {
            a[b.OCCURRENCE] = a[b.OCCURRENCE] || [];
            a[b.OCCURRENCE].push(b);
            return a;
          }, {});
          
          const y = Object.keys(z).map((k: string) => {
            return {
              Line_Type: "DATA_ROW",
              Is_Multi: z[k][0].IS_MULTI,
              Line_no: z[k][0].LINE_NO,
              IS_EDITTED: "N",
              Trans_details_key: z[k][0].TRANS_DETAILS_KEY,
              Occurrence: z[k][0].OCCURRENCE,
              Line_data_occ: z[k]
            };
          });
          
          const c = _.cloneDeep(y[0]);
          c.Line_Type = "HEADER_ROW";
          c.Occurrence = "";
          c.Trans_details_key = "";
          for (let j = 0; j < c.Line_data_occ.length; j++) {
            c.Line_data_occ[j].ATTRIB_VALUE = c.Line_data_occ[j].ATTRIB_DESCRIPTION;
          }
          
          y.unshift(c, {Line_Type: "SUB_ROW", Is_Multi: c.Is_Multi, Line_no: c.Line_no, IS_EDITTED: "N", Trans_details_key: "", Occurrence: "", Line_data_occ: []});
          this.f4562FormData[i].Line_data = _.cloneDeep(y);
          
          // Process METHOD_OR_CONVENTION and AMORTIZATION_PERIOD_OR_PTG
          for (let z = 0; z < this.f4562FormData[i].Line_data.length; z++) {
            if (this.f4562FormData[i].Line_data[z].Line_Type == "DATA_ROW") {
              const line_no = this.f4562FormData[i].Line_data[z].Line_no;
              for (let y = 0; y < this.f4562FormData[i].Line_data[z].Line_data_occ.length; y++) {
                if (this.f4562FormData[i].Line_data[z].Line_data_occ[y].ATTRIB_SHORT_CODE == "METHOD_OR_CONVENTION_" + line_no) {
                  const attrib_vale_array = this.f4562FormData[i].Line_data[z].Line_data_occ[y].ATTRIB_VALUE.split('~');
                  this.f4562FormData[i].Line_data[z].Line_data_occ[y].ATTRIB_VALUE_METHOD = attrib_vale_array[0];
                  this.f4562FormData[i].Line_data[z].Line_data_occ[y].ATTRIB_VALUE_CONVENTION = attrib_vale_array[1];
                } else if (this.f4562FormData[i].Line_data[z].Line_data_occ[y].ATTRIB_SHORT_CODE == "AMORTIZATION_PERIOD_OR_PTG_" + line_no) {
                  const attrib_vale_array = this.f4562FormData[i].Line_data[z].Line_data_occ[y].ATTRIB_VALUE.split('~');
                  this.f4562FormData[i].Line_data[z].Line_data_occ[y].ATTRIB_VALUE_PERIOD = attrib_vale_array[0];
                  this.f4562FormData[i].Line_data[z].Line_data_occ[y].ATTRIB_VALUE_PERCENTAGE = attrib_vale_array[1];
                }
              }
            }
          }
        } else {
          const z = this.f4562FormData[i].Line_data.reduce((a: any, b: any) => {
            a[b.LINE_NO] = a[b.LINE_NO] || [];
            a[b.LINE_NO].push(b);
            return a;
          }, {});
          
          const y = Object.keys(z).map((k: string) => {
            return {
              Line_Type: "HEADER_ROW",
              Is_Multi: z[k][0].IS_MULTI,
              Line_no: z[k][0].LINE_NO,
              IS_EDITTED: "N",
              Trans_details_key: "",
              Occurrence: "",
              Line_data_occ: z[k]
            };
          });
          
          for (let j = 0; j < y[0].Line_data_occ.length; j++) {
            y[0].Line_data_occ[j].ATTRIB_VALUE = y[0].Line_data_occ[j].ATTRIB_DESCRIPTION;
          }
          
          y.push({Line_Type: "SUB_ROW", Is_Multi: y[0].Is_Multi, Line_no: y[0].Line_no, IS_EDITTED: "N", Trans_details_key: "", Occurrence: "", Line_data_occ: []});
          this.f4562FormData[i].Line_data = y;
        }
      } else if (this.f4562FormData[i].ROW_TYPE == 'DT') {
        for (let j = 0; j < this.f4562FormData[i].Line_data.length; j++) {
          if (this.f4562FormData[i].Line_data[j].IS_EDITABLE == 'Y' && 
              this.f4562FormData[i].Line_data[j].EDIT_TYPE == 'currency' && 
              this.f4562FormData[i].Line_data[j].DATA_TYPE == 'number') {
            this.f4562FormData[i].Line_data[j].ATTRIB_VALUE = parseInt(this.f4562FormData[i].Line_data[j].ATTRIB_VALUE);
          }
        }
      } else if (this.f4562FormData[i].ROW_TYPE == 'LH') {
        for (let j = 0; j < this.f4562FormData[i].Line_data.length; j++) {
          if (this.f4562FormData[i].Line_data[j].Line_data) {
            for (let l = 0; l < this.f4562FormData[i].Line_data[j].Line_data.length; l++) {
              if (this.f4562FormData[i].Line_data[j].Line_data[l].IS_EDITABLE == 'Y' && 
                  this.f4562FormData[i].Line_data[j].Line_data[l].EDIT_TYPE == 'currency' && 
                  this.f4562FormData[i].Line_data[j].Line_data[l].DATA_TYPE == 'number') {
                this.f4562FormData[i].Line_data[j].Line_data[l].ATTRIB_VALUE = parseInt(this.f4562FormData[i].Line_data[j].Line_data[l].ATTRIB_VALUE);
              }
            }
          }
        }
      }
    }
    
    this.normalizeLhHeaderLabels();
    this.normalizeLhRowStructure();
    this.calcFinalAmounts();
    this.f4562OriginalData = _.cloneDeep(this.f4562FormData);
    this.cdr.detectChanges();
  }

  private processFACSData(data: any) {
    // Similar processing for FACS data
    this.f4562FACSHeader = data.jsonObject[0];
    this.f4562FACSList = data.jsonObject.slice(1);
    
    // Check valid scenario
    for (let i = 0; i < this.f4562FACSList.length; i++) {
      if (!this.isValidScenario && this.f4562FACSList[i].T_SCENARIO_TYPE_CODE) {
        this.isValidScenario = this.checkEditScenario(
          this.f4562FACSList[i].T_SCENARIO_TYPE_CODE,
          this.f4562FACSList[i].T_SCENARIO,
          this.f4562FACSList[i].ISSUE_KEY
        );
      }
    }
    
    // Process FACS data structure (similar to Input Details)
    const o = this.f4562FACSList.reduce((a: any, b: any) => {
      a[b.LINE_ORDER] = a[b.LINE_ORDER] || [];
      a[b.LINE_ORDER].push(b);
      return a;
    }, {});
    
    const a = Object.keys(o).map((k: string) => {
      return {
        ROW_TYPE: o[k][0].ROW_TYPE,
        DELETE_ROWS: [],
        Line_data: this.dedupeLhCells(o[k])
      };
    });
    
    this.f4562FACSFormData = a;
    
    // Process LH data for FACS (similar logic)
    this.LH_index = this.f4562FACSFormData.findIndex((x: any) => x.ROW_TYPE == "LH");
    
    const n = this.f4562FACSFormData.reduce((a: any, b: any) => {
      a[b.ROW_TYPE] = a[b.ROW_TYPE] || [];
      a[b.ROW_TYPE].push(b);
      return a;
    }, {});
    
    const b = Object.keys(n).map((k: string) => {
      if (k == 'LH') {
        return {ROW_TYPE: n[k][0].ROW_TYPE, DELETE_ROWS: [], Line_data: n[k]};
      }
      return undefined;
    }).filter((item: any) => item !== undefined);
    
    // Remove LH from main array
    for (let i = this.f4562FACSFormData.length - 1; i >= 0; i--) {
      if (this.f4562FACSFormData[i].ROW_TYPE == 'LH') {
        this.f4562FACSFormData.splice(i, 1);
      }
    }
    
    // Process LH data structure for FACS
    for (let g = 0; g < b.length; g++) {
      const bgItem = b[g];
      if (bgItem != undefined && bgItem.Line_data) {
        for (let h = 0; h < bgItem.Line_data.length; h++) {
          const lineDataItem = bgItem.Line_data[h];
          if (lineDataItem && lineDataItem.Line_data && lineDataItem.Line_data[0] && lineDataItem.Line_data[0].OCCURRENCE != null) {
            const z = lineDataItem.Line_data.reduce((a: any, b: any) => {
              a[b.OCCURRENCE] = a[b.OCCURRENCE] || [];
              a[b.OCCURRENCE].push(b);
              return a;
            }, {});
            
            const y = Object.keys(z).map((k: string) => {
              return {ROW_TYPE: z[k][0].ROW_TYPE, DELETE_ROWS: [], Line_data: z[k]};
            });
            
            if (y.length > 0) {
              bgItem.Line_data.splice(h, 1, y[0]);
              if (y.length > 1) {
                for (let a = 1; a < y.length; a++) {
                  bgItem.Line_data.splice(h + a, 0, y[a]);
                }
              }
            }
          }
        }
        this.f4562FACSLHData = bgItem;
      }
    }
    
    // Insert LH data at original index
    if (this.f4562FACSLHData && Object.keys(this.f4562FACSLHData).length > 0) {
      this.f4562FACSFormData.splice(this.LH_index, 0, this.f4562FACSLHData);
    }
    
    // Process CH rows for FACS (similar to Input Details)
    for (let i = 0; i < this.f4562FACSFormData.length; i++) {
      if (this.f4562FACSFormData[i].ROW_TYPE == 'CH') {
        if (this.f4562FACSFormData[i].Line_data[0] && this.f4562FACSFormData[i].Line_data[0].OCCURRENCE != null) {
          const z = this.f4562FACSFormData[i].Line_data.reduce((a: any, b: any) => {
            a[b.OCCURRENCE] = a[b.OCCURRENCE] || [];
            a[b.OCCURRENCE].push(b);
            return a;
          }, {});
          
          const y = Object.keys(z).map((k: string) => {
            return {
              Line_Type: "DATA_ROW",
              Is_Multi: z[k][0].IS_MULTI,
              Line_no: z[k][0].LINE_NO,
              IS_EDITTED: "N",
              Trans_details_key: z[k][0].TRANS_DETAILS_KEY,
              Occurrence: z[k][0].OCCURRENCE,
              Line_data_occ: z[k]
            };
          });
          
          const c = _.cloneDeep(y[0]);
          c.Line_Type = "HEADER_ROW";
          c.Occurrence = "";
          c.Trans_details_key = "";
          for (let j = 0; j < c.Line_data_occ.length; j++) {
            c.Line_data_occ[j].ATTRIB_VALUE = c.Line_data_occ[j].ATTRIB_DESCRIPTION;
          }
          
          y.unshift(c, {Line_Type: "SUB_ROW", Is_Multi: c.Is_Multi, Line_no: c.Line_no, IS_EDITTED: "N", Trans_details_key: "", Occurrence: "", Line_data_occ: []});
          this.f4562FACSFormData[i].Line_data = _.cloneDeep(y);
          
          // Process METHOD_OR_CONVENTION and AMORTIZATION_PERIOD_OR_PTG
          for (let z = 0; z < this.f4562FACSFormData[i].Line_data.length; z++) {
            if (this.f4562FACSFormData[i].Line_data[z].Line_Type == "DATA_ROW") {
              const line_no = this.f4562FACSFormData[i].Line_data[z].Line_no;
              for (let y = 0; y < this.f4562FACSFormData[i].Line_data[z].Line_data_occ.length; y++) {
                if (this.f4562FACSFormData[i].Line_data[z].Line_data_occ[y].ATTRIB_SHORT_CODE == "METHOD_OR_CONVENTION_" + line_no) {
                  const attrib_vale_array = this.f4562FACSFormData[i].Line_data[z].Line_data_occ[y].ATTRIB_VALUE.split('~');
                  this.f4562FACSFormData[i].Line_data[z].Line_data_occ[y].ATTRIB_VALUE_METHOD = attrib_vale_array[0];
                  this.f4562FACSFormData[i].Line_data[z].Line_data_occ[y].ATTRIB_VALUE_CONVENTION = attrib_vale_array[1];
                } else if (this.f4562FACSFormData[i].Line_data[z].Line_data_occ[y].ATTRIB_SHORT_CODE == "AMORTIZATION_PERIOD_OR_PTG_" + line_no) {
                  const attrib_vale_array = this.f4562FACSFormData[i].Line_data[z].Line_data_occ[y].ATTRIB_VALUE.split('~');
                  this.f4562FACSFormData[i].Line_data[z].Line_data_occ[y].ATTRIB_VALUE_PERIOD = attrib_vale_array[0];
                  this.f4562FACSFormData[i].Line_data[z].Line_data_occ[y].ATTRIB_VALUE_PERCENTAGE = attrib_vale_array[1];
                }
              }
            }
          }
        } else {
          const z = this.f4562FACSFormData[i].Line_data.reduce((a: any, b: any) => {
            a[b.LINE_NO] = a[b.LINE_NO] || [];
            a[b.LINE_NO].push(b);
            return a;
          }, {});
          
          const y = Object.keys(z).map((k: string) => {
            return {
              Line_Type: "HEADER_ROW",
              Is_Multi: z[k][0].IS_MULTI,
              Line_no: z[k][0].LINE_NO,
              IS_EDITTED: "N",
              Trans_details_key: "",
              Occurrence: "",
              Line_data_occ: z[k]
            };
          });
          
          for (let j = 0; j < y[0].Line_data_occ.length; j++) {
            y[0].Line_data_occ[j].ATTRIB_VALUE = y[0].Line_data_occ[j].ATTRIB_DESCRIPTION;
          }
          
          y.push({Line_Type: "SUB_ROW", Is_Multi: y[0].Is_Multi, Line_no: y[0].Line_no, IS_EDITTED: "N", Trans_details_key: "", Occurrence: "", Line_data_occ: []});
          this.f4562FACSFormData[i].Line_data = y;
        }
      } else if (this.f4562FACSFormData[i].ROW_TYPE == 'DT') {
        for (let j = 0; j < this.f4562FACSFormData[i].Line_data.length; j++) {
          if (this.f4562FACSFormData[i].Line_data[j].IS_EDITABLE == 'Y' && 
              this.f4562FACSFormData[i].Line_data[j].EDIT_TYPE == 'currency' && 
              this.f4562FACSFormData[i].Line_data[j].DATA_TYPE == 'number') {
            this.f4562FACSFormData[i].Line_data[j].ATTRIB_VALUE = parseInt(this.f4562FACSFormData[i].Line_data[j].ATTRIB_VALUE);
          }
        }
      } else if (this.f4562FACSFormData[i].ROW_TYPE == 'LH') {
        for (let j = 0; j < this.f4562FACSFormData[i].Line_data.length; j++) {
          if (this.f4562FACSFormData[i].Line_data[j].Line_data) {
            for (let l = 0; l < this.f4562FACSFormData[i].Line_data[j].Line_data.length; l++) {
              if (this.f4562FACSFormData[i].Line_data[j].Line_data[l].IS_EDITABLE == 'Y' && 
                  this.f4562FACSFormData[i].Line_data[j].Line_data[l].EDIT_TYPE == 'currency' && 
                  this.f4562FACSFormData[i].Line_data[j].Line_data[l].DATA_TYPE == 'number') {
                this.f4562FACSFormData[i].Line_data[j].Line_data[l].ATTRIB_VALUE = parseInt(this.f4562FACSFormData[i].Line_data[j].Line_data[l].ATTRIB_VALUE);
              }
            }
          }
        }
      }
    }
    
    this.normalizeLhHeaderLabels(this.f4562FACSFormData);
    this.normalizeLhRowStructure(this.f4562FACSFormData);
    this.cdr.detectChanges();
  }

  checkEditScenario(s_type_code: string, t_scenario: string, t_issue: string): boolean {
    if (this.scenario == t_scenario && ((s_type_code == 'EXAM' && this.glbl_issue_id == t_issue) || s_type_code != 'EXAM')) {
      return true;
    }
    return false;
  }

  private normalizeLhHeaderLabels(formData: F4562DataNode[] = this.f4562FormData): void {
    const lhRow = formData.find(row => row.ROW_TYPE === 'LH');
    if (!lhRow?.Line_data?.length) {
      return;
    }
    for (const lineRow of lhRow.Line_data) {
      for (const cell of lineRow.Line_data || []) {
        if (cell.ATTRIB_SHORT_CODE?.includes('HEADER') && cell.ATTRIB_DESCRIPTION) {
          cell.ATTRIB_VALUE = cell.ATTRIB_DESCRIPTION;
        }
      }
    }
  }

  private static readonly LH_DATA_COLUMN_ORDERS = [1, 3, 4, 5, 6, 7, 8, 9];

  private dedupeLhCells(cells: F4562DataNode[]): F4562DataNode[] {
    const seen = new Set<string>();
    return (cells || []).filter(cell => {
      const key = String(cell?.LINE_ATTRIB_KEY ?? cell?.ATTRIB_SHORT_CODE ?? '');
      if (!key || seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  private isLhHeaderRow(cells: F4562DataNode[]): boolean {
    return (cells || []).some(cell => cell.ATTRIB_SHORT_CODE?.includes('HEADER'));
  }

  private isLhSectionTitleRow(cells: F4562DataNode[]): boolean {
    const rowCells = cells || [];
    if (rowCells.length > 3) {
      return false;
    }
    const propertyCell = rowCells.find(cell => cell.ATTRIB_SHORT_CODE?.startsWith('PROPERTY_DESC_'));
    if (!propertyCell) {
      return false;
    }
    const colspan = Number(propertyCell.COLSPAN);
    return rowCells.length <= 2 || colspan >= 7;
  }

  private isLhMacrsDataRow(cells: F4562DataNode[]): boolean {
    const rowCells = cells || [];
    if (this.isLhHeaderRow(rowCells) || this.isLhSectionTitleRow(rowCells)) {
      return false;
    }
    return rowCells.some(cell => cell.ATTRIB_SHORT_CODE?.startsWith('LINE_NO_')) &&
      rowCells.some(cell => cell.ATTRIB_SHORT_CODE?.startsWith('PROPERTY_DESC_'));
  }

  private clearLhCellColspan(cell: F4562DataNode): void {
    if (cell && Object.prototype.hasOwnProperty.call(cell, 'COLSPAN')) {
      delete cell.COLSPAN;
    }
  }

  private normalizeLhRowStructure(formData: F4562DataNode[] = this.f4562FormData): void {
    const lhRow = formData.find(row => row.ROW_TYPE === 'LH');
    if (!lhRow?.Line_data?.length) {
      return;
    }

    for (const lineRow of lhRow.Line_data) {
      if (!lineRow?.Line_data?.length) {
        continue;
      }

      let cells = this.dedupeLhCells(lineRow.Line_data);

      if (this.isLhHeaderRow(cells)) {
        cells.sort((a, b) => Number(a.ATTRIB_ORDER) - Number(b.ATTRIB_ORDER));
        cells.forEach(cell => this.clearLhCellColspan(cell));
        lineRow.Line_data = cells;
        continue;
      }

      if (this.isLhSectionTitleRow(cells)) {
        cells.sort((a, b) => Number(a.ATTRIB_ORDER) - Number(b.ATTRIB_ORDER));
        const propertyCell = cells.find(cell => cell.ATTRIB_SHORT_CODE?.startsWith('PROPERTY_DESC_'));
        if (propertyCell) {
          propertyCell.COLSPAN = 7;
        }
        cells.forEach(cell => {
          if (!cell.ATTRIB_SHORT_CODE?.startsWith('PROPERTY_DESC_')) {
            this.clearLhCellColspan(cell);
          }
        });
        lineRow.Line_data = cells.slice(0, 2);
        continue;
      }

      if (this.isLhMacrsDataRow(cells)) {
        const byOrder = new Map<number, F4562DataNode>();
        for (const cell of cells) {
          const order = Number(cell.ATTRIB_ORDER);
          if (!Number.isNaN(order) && !byOrder.has(order)) {
            byOrder.set(order, cell);
          }
        }

        const normalized: F4562DataNode[] = [];
        for (const order of F4562Component.LH_DATA_COLUMN_ORDERS) {
          const cell = byOrder.get(order);
          if (cell) {
            this.clearLhCellColspan(cell);
            normalized.push(cell);
          }
        }

        if (normalized.length >= 4) {
          lineRow.Line_data = normalized;
        }
      }
    }
  }

  getLhCellColspan(dataItem: F4562DataNode, lineCells: F4562DataNode[]): number | null {
    if (!dataItem || !this.isLhSectionTitleRow(lineCells || [])) {
      return null;
    }
    if (dataItem.ATTRIB_SHORT_CODE?.startsWith('PROPERTY_DESC_')) {
      return 7;
    }
    return null;
  }

  getLhCellTrackKey(dataItem: F4562DataNode, index: number): string | number {
    return dataItem?.LINE_ATTRIB_KEY ?? dataItem?.ATTRIB_SHORT_CODE ?? index;
  }

  getHeaderCellText(dataItem: F4562DataNode): string {
    if (!dataItem) {
      return '';
    }
    if (dataItem.ATTRIB_DESCRIPTION) {
      return String(dataItem.ATTRIB_DESCRIPTION);
    }
    return dataItem.ATTRIB_VALUE != null ? String(dataItem.ATTRIB_VALUE) : '';
  }

  hasVisibleRowContent(row: F4562DataNode): boolean {
    if (!row?.Line_data?.length) {
      return false;
    }
    if (row.ROW_TYPE === 'PH') {
      const value = row.Line_data[0]?.ATTRIB_VALUE;
      return value !== null && value !== undefined && String(value).trim() !== '';
    }
    return row.ROW_TYPE === 'TH' ||
      row.ROW_TYPE === 'DT' ||
      row.ROW_TYPE === 'RT' ||
      row.ROW_TYPE === 'CH' ||
      row.ROW_TYPE === 'LH';
  }

  onTabChange(tabName: string) {
    this.selectedTabName = tabName;
    this.currentTabName = tabName;
    setTimeout(() => {
      const movingTab = document.querySelector('.wizard-tabs .nav-pills .wizard-tab-hilighter');
      const selectedTab = document.querySelector('.wizard-tabs .uib-tab.nav-item.active');
      if (movingTab && selectedTab) {
        // Tab animation logic (if needed)
      }
    }, 50);
  }

  getUnallocatedAmt() {
    for (let i = 0; i < this.f4562FormData.length; i++) {
      if (this.f4562FormData[i].ROW_TYPE == 'TH') {
        if (this.f4562FormData[i].Line_data[1] && 
            this.f4562FormData[i].Line_data[1].DESCRIPTION == 'Unallocated Amount' && 
            this.f4562FormData[i].Line_data[1].DATA_TYPE == 'number' && 
            this.f4562FormData[i].Line_data[1].ATTRIB_VALUE != null && 
            this.f4562FormData[i].Line_data[1].ATTRIB_VALUE != undefined) {
          this.unAllocatedAmt = parseInt(this.f4562FormData[i].Line_data[1].ATTRIB_VALUE);
        }
      }
    }
  }

  AddNewRow(line_no: string) {
    let occurrance = 1;
    let trans_details_key: any = null;
    
    for (let i = 0; i < this.f4562FormData.length; i++) {
      if (this.f4562FormData[i].ROW_TYPE == 'CH') {
        const lastItem = this.f4562FormData[i].Line_data[this.f4562FormData[i].Line_data.length - 1];
        if (lastItem && lastItem.Occurrence != null && lastItem.Occurrence != "" && lastItem.Occurrence != undefined) {
          occurrance = parseInt(lastItem.Occurrence) + 1;
          trans_details_key = lastItem.Trans_details_key;
        }
        
        const new_row = _.cloneDeep(this.f4562FormData[i].Line_data[0]);
        new_row.Line_Type = "NEW_EDIT_ROW";
        new_row.Occurrence = occurrance;
        new_row.Trans_details_key = trans_details_key;
        new_row.IS_EDITTED = "Y";
        
        if (new_row.Line_no == line_no) {
          for (let j = 0; j < new_row.Line_data_occ.length; j++) {
            if (new_row.Line_data_occ[j].EDIT_TYPE == "custom_date_various" && new_row.Line_data_occ[j].DATA_TYPE == "dateVarious") {
              // Convert current date to YYYY-MM-DD format for HTML date input
              new_row.Line_data_occ[j].ATTRIB_VALUE = this.convertToHtmlDateFormat(new Date());
            } else if (new_row.Line_data_occ[j].EDIT_TYPE == "currency" && new_row.Line_data_occ[j].DATA_TYPE == "number") {
              new_row.Line_data_occ[j].ATTRIB_VALUE = 0;
            } else if (new_row.Line_data_occ[j].EDIT_TYPE == "label" && new_row.Line_data_occ[j].DATA_TYPE == "string") {
              // Keep as is
            } else {
              new_row.Line_data_occ[j].ATTRIB_VALUE = null;
            }
            new_row.Line_data_occ[j].OCCURRENCE = occurrance;
            new_row.Line_data_occ[j].TRANS_DETAILS_KEY = trans_details_key;
            new_row.Line_data_occ[j].IS_CHANGED_FLAG = "Y";
          }
          this.f4562FormData[i].Line_data.push(new_row);
        }
      }
    }
    this.cdr.detectChanges();
  }

  editExistingRecord(line_no: string, occurrence: any, index: number) {
    for (let i = 0; i < this.f4562FormData.length; i++) {
      if (this.f4562FormData[i].ROW_TYPE == 'CH') {
        for (let j = 0; j < this.f4562FormData[i].Line_data.length; j++) {
          if (j == index || j == index + 1) {
            if (this.f4562FormData[i].Line_data[j].Line_Type == "DATA_ROW" && 
                this.f4562FormData[i].Line_data[j].Line_no == line_no && 
                this.f4562FormData[i].Line_data[j].Occurrence == occurrence) {
              this.f4562FormData[i].Line_data[j].Line_Type = "NEW_EDIT_ROW";
              for (let l = 0; l < this.f4562FormData[i].Line_data[j].Line_data_occ.length; l++) {
                if (this.f4562FormData[i].Line_data[j].Line_data_occ[l].EDIT_TYPE == "currency" && 
                    this.f4562FormData[i].Line_data[j].Line_data_occ[l].DATA_TYPE == "number") {
                  this.f4562FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE = parseInt(this.f4562FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE);
                } else if (this.f4562FormData[i].Line_data[j].Line_data_occ[l].EDIT_TYPE == "custom_date_various" && 
                           this.f4562FormData[i].Line_data[j].Line_data_occ[l].DATA_TYPE == "dateVarious") {
                  if (this.f4562FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE != "VARIOUS") {
                    // Convert to YYYY-MM-DD format for HTML date input
                    this.f4562FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE = 
                      this.convertToHtmlDateFormat(this.f4562FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE);
                  }
                }
              }
            }
          }
        }
      }
    }
    this.cdr.detectChanges();
  }

  removeExistingRecord(line_no: string, Occurrence: any, index: number) {
    for (let i = 0; i < this.f4562FormData.length; i++) {
      if (this.f4562FormData[i].ROW_TYPE == 'CH') {
        for (let j = 0; j < this.f4562FormData[i].Line_data.length; j++) {
          if (j == index) {
            if ((this.f4562FormData[i].Line_data[j].Line_Type == "DATA_ROW" || 
                 this.f4562FormData[i].Line_data[j].Line_Type == "NEW_EDIT_ROW") && 
                this.f4562FormData[i].Line_data[j].Line_no == line_no && 
                null != this.f4562FormData[i].Line_data[j].Occurrence && 
                this.f4562FormData[i].Line_data[j].Occurrence != "" && 
                this.f4562FormData[i].Line_data[j].Occurrence == Occurrence) {
              this.f4562FormData[i].DELETE_ROWS.push(this.f4562FormData[i].Line_data[j]);
              this.f4562FormData[i].Line_data.splice(j, 1);
            } else if ((this.f4562FormData[i].Line_data[j].Line_Type == "DATA_ROW" || 
                       this.f4562FormData[i].Line_data[j].Line_Type == "NEW_EDIT_ROW") && 
                       this.f4562FormData[i].Line_data[j].Line_no == line_no && 
                       (Occurrence == null || Occurrence == "")) {
              this.f4562FormData[i].Line_data.splice(j, 1);
            }
          }
        }
      }
    }
    this.calcFinalAmounts();
    this.cdr.detectChanges();
  }

  saveNewEdittedRecord(line_no: string, occurrence: any, index: number) {
    for (let i = 0; i < this.f4562FormData.length; i++) {
      if (this.f4562FormData[i].ROW_TYPE == 'CH') {
        for (let j = 0; j < this.f4562FormData[i].Line_data.length; j++) {
          if (j == index) {
            if (this.f4562FormData[i].Line_data[j].Line_Type == "NEW_EDIT_ROW" && 
                this.f4562FormData[i].Line_data[j].Line_no == line_no && 
                this.f4562FormData[i].Line_data[j].Occurrence == occurrence) {
              this.f4562FormData[i].Line_data[j].Line_Type = "DATA_ROW";
            }
            for (let l = 0; l < this.f4562FormData[i].Line_data[j].Line_data_occ.length; l++) {
              if (this.f4562FormData[i].Line_data[j].Line_data_occ[l].IS_EDITABLE_CELL == "Y" && 
                  this.f4562FormData[i].Line_data[j].Line_data_occ[l].DATA_TYPE == "dateVarious" && 
                  this.f4562FormData[i].Line_data[j].Line_data_occ[l].EDIT_TYPE == "custom_date_various") {
                this.f4562FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE = this.getDateFormat(this.f4562FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE);
              }
              if (this.f4562FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_SHORT_CODE == "METHOD_OR_CONVENTION_" + line_no) {
                if (this.f4562FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE_METHOD == undefined) {
                  this.f4562FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE_METHOD = '';
                }
                if (this.f4562FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE_CONVENTION == undefined) {
                  this.f4562FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE_CONVENTION = '';
                }
                this.f4562FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE = 
                  this.f4562FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE_METHOD + '~' + 
                  this.f4562FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE_CONVENTION;
              } else if (this.f4562FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_SHORT_CODE == "AMORTIZATION_PERIOD_OR_PTG_" + line_no) {
                if (this.f4562FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE_PERIOD == undefined) {
                  this.f4562FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE_PERIOD = '';
                }
                if (this.f4562FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE_PERCENTAGE == undefined) {
                  this.f4562FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE_PERCENTAGE = '';
                }
                this.f4562FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE = 
                  this.f4562FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE_PERIOD + '~' + 
                  this.f4562FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE_PERCENTAGE;
              }
            }
          }
        }
      }
    }
    this.cdr.detectChanges();
  }

  valuechanged(parent_index: number, occurrence: any, line_no: string, index: number) {
    for (let i = 0; i < this.f4562FormData.length; i++) {
      if (this.f4562FormData[i].ROW_TYPE == 'CH') {
        if (this.f4562FormData[i].Line_data[parent_index] != null && 
            this.f4562FormData[i].Line_data[parent_index] != undefined) {
          if (this.f4562FormData[i].Line_data[parent_index].Line_no == line_no && 
              this.f4562FormData[i].Line_data[parent_index].Occurrence == occurrence) {
            if (this.f4562FormData[i].Line_data[parent_index].Line_data_occ[index + 1]) {
              this.f4562FormData[i].Line_data[parent_index].Line_data_occ[index + 1].IS_CHANGED_FLAG = "Y";
              this.f4562FormData[i].Line_data[parent_index].Line_data_occ[index + 1].HAS_ERROR = "N";
            }
          }
        }
      } else if (this.f4562FormData[i].ROW_TYPE == 'DT' || this.f4562FormData[i].ROW_TYPE == 'RT') {
        if (i == parent_index && 
            this.f4562FormData[i].Line_data[index] != null && 
            this.f4562FormData[i].Line_data[index] != undefined) {
          this.f4562FormData[i].Line_data[index].IS_CHANGED_FLAG = "Y";
        }
      } else if (this.f4562FormData[i].ROW_TYPE == 'LH') {
        if (this.f4562FormData[i].Line_data[parent_index] != null && 
            this.f4562FormData[i].Line_data[parent_index] != undefined) {
          if (this.f4562FormData[i].Line_data[parent_index].Line_data[index] != null && 
              this.f4562FormData[i].Line_data[parent_index].Line_data[index].LINE_NO == line_no) {
            // Set dropdown changed flag for specific line numbers
            const lhLineNo = this.f4562FormData[i].Line_data[parent_index].Line_data[index].LINE_NO;
            if ([
              '19a', '19b', '19c', '19d', '19e', '19f', '19g', '19h', '19i',
              '19j(1)', '19j(2)', '20a', '20b', '20c', '20d'
            ].includes(lhLineNo) || /^19j\(\d+\)$/.test(lhLineNo)) {
              for (let k = 0; k < this.f4562FormData[i].Line_data[parent_index].Line_data.length; k++) {
                if (this.f4562FormData[i].Line_data[parent_index].Line_data[k].EDIT_TYPE === 'select' && 
                    this.f4562FormData[i].Line_data[parent_index].Line_data[k].DATA_TYPE === 'string' && 
                    this.f4562FormData[i].Line_data[parent_index].Line_data[k].IS_EDITABLE == 'Y') {
                  this.f4562FormData[i].Line_data[parent_index].Line_data[k].IS_CHANGED_FLAG = "Y";
                }
              }
            }
            this.f4562FormData[i].Line_data[parent_index].Line_data[index].IS_CHANGED_FLAG = "Y";
          }
        }
      }
    }
    this.cdr.detectChanges();
  }

  onCurrencyAmountChange(
    mainIndex: number,
    itemIndex: number,
    lineNo: string,
    parentIndex?: number,
    occurrence: unknown = null,
    valueChangeIndex?: number,
    valueChangeParentIndex?: number
  ): void {
    this.valuechanged(
      valueChangeParentIndex ?? mainIndex,
      occurrence,
      lineNo,
      valueChangeIndex ?? itemIndex
    );
    this.updateAmount(itemIndex, lineNo, mainIndex, parentIndex);
  }

  updateAmount(index: number, line_no: string, mainIndex?: number, parentIndex?: number) {
    for (let i = 0; i < this.f4562FormData.length; i++) {
      if (this.f4562FormData[i].ROW_TYPE == 'CH' && 
          this.f4562FormData[i].Line_data[0] && 
          this.f4562FormData[i].Line_data[0].Line_no == line_no) {
        for (let j = 0; j < this.f4562FormData[i].Line_data[index].Line_data_occ.length; j++) {
          if (this.f4562FormData[i].Line_data[index].Line_data_occ[j].ATTRIB_CALC_FORMULA != null) {
            const line_data_values = this.f4562FormData[i].Line_data[index].Line_data_occ;
            this.f4562FormData[i].Line_data[index].Line_data_occ[j].ATTRIB_VALUE = 
              this.calcLineFormula(
                this.f4562FormData[i].Line_data[index].Line_data_occ[j].ATTRIB_CALC_FORMULA, 
                line_data_values
              );
            this.f4562FormData[i].Line_data[index].Line_data_occ[j].IS_CHANGED_FLAG = "Y";
          }
        }
      }
    }
    this.calcFinalAmounts(line_no);
    this.cdr.detectChanges();
  }

  calcLineFormula(formula: string, line_data_values: any[]): number {
    const lineFormulaArray = formula.split(/[+-/\\*\\]/);
    for (let i = 0; i < lineFormulaArray.length; i++) {
      if (lineFormulaArray[i].indexOf("(") > -1 && lineFormulaArray[i].indexOf(")") > -1) {
        lineFormulaArray[i] = lineFormulaArray[i].replace(/"/g, "").replace(/'/g, "").replace(/\(|\)/g, "");
        const currentTabAtrribVal = line_data_values.filter((item: any) => 
          item.ATTRIB_SHORT_CODE == lineFormulaArray[i].trim()
        );
        if (currentTabAtrribVal.length > 0) {
          const lineAmt = parseInt(currentTabAtrribVal[0].ATTRIB_VALUE);
          if (parseInt(lineAmt.toString()) > 0) {
            formula = formula.replace("(" + lineFormulaArray[i].trim() + ")", lineAmt.toString());
          } else {
            formula = formula.replace("(" + lineFormulaArray[i].trim() + ")", "(" + lineAmt.toString() + ")");
          }
        }
      }
    }
    
    try {
      const result = safeEvalFormula(formula, null, 'f-4562');
      if (result != null && typeof result !== 'undefined' && parseInt(result.toString()) === parseInt(result.toString())) {
        return parseInt(result.toString());
      }
    } catch (e) {
      console.error('Error in calculation:', e);
    }
    return 0;
  }

  calcFinalAmounts(skipLineNo?: string) {
    for (let i = 0; i < this.f4562FormData.length; i++) {
      if (this.f4562FormData[i].ROW_TYPE == 'TH' || this.f4562FormData[i].ROW_TYPE == 'DT') {
        for (let j = 0; j < this.f4562FormData[i].Line_data.length; j++) {
          const line = this.f4562FormData[i].Line_data[j];
          if (line.ATTRIB_CALC_FORMULA != null) {
            if (skipLineNo && line.LINE_NO === skipLineNo && line.IS_EDITABLE === 'Y') {
              continue;
            }
            line.ATTRIB_VALUE = this.calc_final_amt(line.ATTRIB_CALC_FORMULA);
            line.IS_CHANGED_FLAG = "Y";
          }
        }
      }
    }
    this.getUnallocatedAmt();
    this.cdr.detectChanges();
  }

  calc_final_amt(formula: string): number {
    const lineFormulaArray = formula.split(/[+-/\\*\\]/);
    for (let i = 0; i < lineFormulaArray.length; i++) {
      let lineAmt = 0;
      if (lineFormulaArray[i].indexOf("(") > -1 && lineFormulaArray[i].indexOf(")") > -1) {
        lineFormulaArray[i] = lineFormulaArray[i].replace(/"/g, "").replace(/'/g, "").replace(/\(|\)/g, "");
        for (let k = 0; k < this.f4562FormData.length; k++) {
          if (this.f4562FormData[k].ROW_TYPE == 'CH') {
            for (let j = 0; j < this.f4562FormData[k].Line_data.length; j++) {
              if (this.f4562FormData[k].Line_data[j].Line_Type == "DATA_ROW" || 
                  this.f4562FormData[k].Line_data[j].Line_Type == "NEW_EDIT_ROW") {
                const row_data = this.f4562FormData[k].Line_data[j].Line_data_occ;
                const currentTabAtrribVal = row_data.filter((item: any) => 
                  item.ATTRIB_SHORT_CODE == lineFormulaArray[i].trim()
                );
                if (currentTabAtrribVal.length > 0) {
                  lineAmt = lineAmt + parseInt(currentTabAtrribVal[0].ATTRIB_VALUE);
                }
              }
            }
          } else if (this.f4562FormData[k].ROW_TYPE == 'LH') {
            for (let j = 0; j < this.f4562FormData[k].Line_data.length; j++) {
              const row_data = this.f4562FormData[k].Line_data[j].Line_data;
              const currentTabAtrribVal = row_data.filter((item: any) => 
                item.ATTRIB_SHORT_CODE == lineFormulaArray[i].trim()
              );
              if (currentTabAtrribVal.length > 0) {
                lineAmt = lineAmt + parseInt(currentTabAtrribVal[0].ATTRIB_VALUE);
              }
            }
          } else {
            const row_data = this.f4562FormData[k].Line_data;
            const currentTabAtrribVal = row_data.filter((item: any) => 
              item.ATTRIB_SHORT_CODE == lineFormulaArray[i].trim()
            );
            if (currentTabAtrribVal.length > 0) {
              lineAmt = lineAmt + parseInt(currentTabAtrribVal[0].ATTRIB_VALUE);
            }
          }
        }
        if (parseInt(lineAmt.toString()) > 0) {
          formula = formula.replace("(" + lineFormulaArray[i].trim() + ")", lineAmt.toString());
        } else {
          formula = formula.replace("(" + lineFormulaArray[i].trim() + ")", "(" + lineAmt.toString() + ")");
        }
      }
    }
    
    try {
      const result = safeEvalFormula(formula, null, 'f-4562');
      if (result != null && typeof result !== 'undefined' && parseInt(result.toString()) === parseInt(result.toString())) {
        return parseInt(result.toString());
      }
    } catch (e) {
      console.error('Error in final calculation:', e);
    }
    return 0;
  }

  getDateFormat(date_value: any): string {
    if (date_value == "VARIOUS") {
      return "VARIOUS";
    }
    if (date_value instanceof Date) {
      const month = (date_value.getMonth() > 8) ? (date_value.getMonth() + 1).toString() : ('0' + (date_value.getMonth() + 1).toString());
      const day = (date_value.getDate() > 9) ? date_value.getDate().toString() : ('0' + date_value.getDate().toString());
      return month + '/' + day + '/' + date_value.getFullYear().toString();
    }
    return date_value;
  }

  // Convert date to YYYY-MM-DD format for HTML date input
  convertToHtmlDateFormat(date_value: any): string {
    if (date_value == "VARIOUS" || !date_value) {
      return "";
    }
    
    let dateObj: Date;
    
    // If it's already a Date object, use it
    if (date_value instanceof Date) {
      dateObj = date_value;
    } else {
      // Try to parse the date string
      dateObj = new Date(date_value);
    }
    
    // Check if valid date
    if (isNaN(dateObj.getTime())) {
      return "";
    }
    
    // Format as YYYY-MM-DD
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  }

  updateFormDataValues() {
    for (let i = 0; i < this.f4562FinalData.length; i++) {
      if (this.f4562FinalData[i].ROW_TYPE == 'CH') {
        for (let j = 0; j < this.f4562FinalData[i].Line_data.length; j++) {
          if (this.f4562FinalData[i].Line_data[j].Line_Type == "NEW_EDIT_ROW") {
            for (let l = 0; l < this.f4562FinalData[i].Line_data[j].Line_data_occ.length; l++) {
              const line_no = this.f4562FinalData[i].Line_data[j].Line_data_occ[l].LINE_NO;
              if (this.f4562FinalData[i].Line_data[j].Line_data_occ[l].IS_EDITABLE_CELL == "Y" && 
                  this.f4562FinalData[i].Line_data[j].Line_data_occ[l].DATA_TYPE == "dateVarious" && 
                  this.f4562FinalData[i].Line_data[j].Line_data_occ[l].EDIT_TYPE == "custom_date_various") {
                this.f4562FinalData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE = 
                  this.getDateFormat(this.f4562FinalData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE);
              }
              if (this.f4562FinalData[i].Line_data[j].Line_data_occ[l].ATTRIB_SHORT_CODE == "METHOD_OR_CONVENTION_" + line_no) {
                if (this.f4562FinalData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE_METHOD == undefined) {
                  this.f4562FinalData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE_METHOD = '';
                }
                if (this.f4562FinalData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE_CONVENTION == undefined) {
                  this.f4562FinalData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE_CONVENTION = '';
                }
                this.f4562FinalData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE = 
                  this.f4562FinalData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE_METHOD + '~' + 
                  this.f4562FinalData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE_CONVENTION;
              } else if (this.f4562FinalData[i].Line_data[j].Line_data_occ[l].ATTRIB_SHORT_CODE == "AMORTIZATION_PERIOD_OR_PTG_" + line_no) {
                if (this.f4562FinalData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE_PERIOD == undefined) {
                  this.f4562FinalData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE_PERIOD = '';
                }
                if (this.f4562FinalData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE_PERCENTAGE == undefined) {
                  this.f4562FinalData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE_PERCENTAGE = '';
                }
                this.f4562FinalData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE = 
                  this.f4562FinalData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE_PERIOD + '~' + 
                  this.f4562FinalData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE_PERCENTAGE;
              }
            }
          }
        }
      }
    }
  }

  validateFormValues(): number {
    let returnValue = 0;
    for (let i = 0; i < this.f4562FormData.length; i++) {
      if (this.f4562FormData[i].ROW_TYPE == 'CH') {
        for (let j = 0; j < this.f4562FormData[i].Line_data.length; j++) {
          const line_no = this.f4562FormData[i].Line_data[j].Line_no;
          if (this.f4562FormData[i].Line_data[j].Line_Type == "DATA_ROW" || 
              this.f4562FormData[i].Line_data[j].Line_Type == "NEW_EDIT_ROW") {
            const property_desc_index = this.f4562FormData[i].Line_data[j].Line_data_occ.findIndex((x: any) => 
              x.ATTRIB_SHORT_CODE == "PROPERTY_DESC_" + line_no
            );
            const recovery_period_index = this.f4562FormData[i].Line_data[j].Line_data_occ.findIndex((x: any) => 
              x.ATTRIB_SHORT_CODE == "RECOVERY_PERIOD_" + line_no
            );
            const method_convention_index = this.f4562FormData[i].Line_data[j].Line_data_occ.findIndex((x: any) => 
              x.ATTRIB_SHORT_CODE == "METHOD_OR_CONVENTION_" + line_no
            );
            const desc_costs_index = this.f4562FormData[i].Line_data[j].Line_data_occ.findIndex((x: any) => 
              x.ATTRIB_SHORT_CODE == "DESCRIPTION_COSTS_" + line_no
            );
            const code_section_index = this.f4562FormData[i].Line_data[j].Line_data_occ.findIndex((x: any) => 
              x.ATTRIB_SHORT_CODE == "CODE_SECTION_" + line_no
            );
            const period_percentage_index = this.f4562FormData[i].Line_data[j].Line_data_occ.findIndex((x: any) => 
              x.ATTRIB_SHORT_CODE == "AMORTIZATION_PERIOD_OR_PTG_" + line_no
            );
            
            if (null != property_desc_index && property_desc_index != -1 && 
                (this.f4562FormData[i].Line_data[j].Line_data_occ[property_desc_index].ATTRIB_VALUE == null || 
                 this.f4562FormData[i].Line_data[j].Line_data_occ[property_desc_index].ATTRIB_VALUE == undefined || 
                 this.f4562FormData[i].Line_data[j].Line_data_occ[property_desc_index].ATTRIB_VALUE.length <= 0)) {
              this.f4562FormData[i].Line_data[j].Line_data_occ[property_desc_index].HAS_ERROR = "Y";
              this.f4562FormData[i].Line_data[j].Line_data_occ[property_desc_index].ATTRIB_VALUE_ERROR = "Enter Property Type Here.";
              returnValue = 1;
            }
            if (null != desc_costs_index && desc_costs_index != -1 && 
                (this.f4562FormData[i].Line_data[j].Line_data_occ[desc_costs_index].ATTRIB_VALUE == null || 
                 this.f4562FormData[i].Line_data[j].Line_data_occ[desc_costs_index].ATTRIB_VALUE == undefined || 
                 this.f4562FormData[i].Line_data[j].Line_data_occ[desc_costs_index].ATTRIB_VALUE.length <= 0)) {
              this.f4562FormData[i].Line_data[j].Line_data_occ[desc_costs_index].HAS_ERROR = "Y";
              this.f4562FormData[i].Line_data[j].Line_data_occ[desc_costs_index].ATTRIB_VALUE_ERROR = "Enter Description of Amortization Costs Here.";
              returnValue = 1;
            }
            if (null != recovery_period_index && recovery_period_index != -1 && 
                (this.f4562FormData[i].Line_data[j].Line_data_occ[recovery_period_index].ATTRIB_VALUE == null || 
                 this.f4562FormData[i].Line_data[j].Line_data_occ[recovery_period_index].ATTRIB_VALUE == undefined || 
                 this.f4562FormData[i].Line_data[j].Line_data_occ[recovery_period_index].ATTRIB_VALUE.length <= 0)) {
              this.f4562FormData[i].Line_data[j].Line_data_occ[recovery_period_index].HAS_ERROR = "Y";
              this.f4562FormData[i].Line_data[j].Line_data_occ[recovery_period_index].ATTRIB_VALUE_ERROR = "Select the Recovery Period for the Depreciation.";
              returnValue = 1;
            }
            if (null != code_section_index && code_section_index != -1 && 
                (this.f4562FormData[i].Line_data[j].Line_data_occ[code_section_index].ATTRIB_VALUE == null || 
                 this.f4562FormData[i].Line_data[j].Line_data_occ[code_section_index].ATTRIB_VALUE == undefined || 
                 this.f4562FormData[i].Line_data[j].Line_data_occ[code_section_index].ATTRIB_VALUE.length <= 0)) {
              this.f4562FormData[i].Line_data[j].Line_data_occ[code_section_index].HAS_ERROR = "Y";
              this.f4562FormData[i].Line_data[j].Line_data_occ[code_section_index].ATTRIB_VALUE_ERROR = "Enter the Code Section for the Amortization.";
              returnValue = 1;
            }
            if (null != method_convention_index && method_convention_index != -1) {
              if (this.f4562FormData[i].Line_data[j].Line_data_occ[method_convention_index].ATTRIB_VALUE_METHOD == null || 
                  this.f4562FormData[i].Line_data[j].Line_data_occ[method_convention_index].ATTRIB_VALUE_METHOD == undefined) {
                this.f4562FormData[i].Line_data[j].Line_data_occ[method_convention_index].HAS_ERROR = "Y";
                this.f4562FormData[i].Line_data[j].Line_data_occ[method_convention_index].ATTRIB_VALUE_ERROR = "Select the Method for the Depreciation.";
                returnValue = 1;
              }
              if (this.f4562FormData[i].Line_data[j].Line_data_occ[method_convention_index].ATTRIB_VALUE_CONVENTION == null || 
                  this.f4562FormData[i].Line_data[j].Line_data_occ[method_convention_index].ATTRIB_VALUE_CONVENTION == undefined) {
                this.f4562FormData[i].Line_data[j].Line_data_occ[method_convention_index].HAS_ERROR = "Y";
                this.f4562FormData[i].Line_data[j].Line_data_occ[method_convention_index].ATTRIB_VALUE_ERROR = "Select the Convention for the Depreciation.";
                returnValue = 1;
              }
            }
            if (null != period_percentage_index && period_percentage_index != -1) {
              if ((this.f4562FormData[i].Line_data[j].Line_data_occ[period_percentage_index].ATTRIB_VALUE_PERIOD == null || 
                   this.f4562FormData[i].Line_data[j].Line_data_occ[period_percentage_index].ATTRIB_VALUE_PERIOD == undefined) &&
                  (this.f4562FormData[i].Line_data[j].Line_data_occ[period_percentage_index].ATTRIB_VALUE_PERCENTAGE == null || 
                   this.f4562FormData[i].Line_data[j].Line_data_occ[period_percentage_index].ATTRIB_VALUE_PERCENTAGE == undefined)) {
                this.f4562FormData[i].Line_data[j].Line_data_occ[period_percentage_index].HAS_ERROR = "Y";
                this.f4562FormData[i].Line_data[j].Line_data_occ[period_percentage_index].ATTRIB_VALUE_ERROR = "Select the Amortizarion Period or Enter Percentage for the Depreciation.";
                returnValue = 1;
              }
            }
          }
        }
      }
    }
    return returnValue;
  }

  save() {
    this.f4562FinalData = [];
    const clob_settings: any[] = [];
    
    if (this.crudLoading) {
      this._snackBar.open("Please wait while we save this request", 'Close', {
        duration: 4000,
        panelClass: ['info-snackbar']
      });
      return;
    }
    
    this.f4562FinalData = _.cloneDeep(this.f4562FormData);
    
    if (_.isEqual(this.f4562FormData, this.f4562OriginalData)) {
      this.cancel();
      return;
    }
    
    this.updateFormDataValues();
    const returnValue = this.validateFormValues();
    if (returnValue == 1) {
      this._snackBar.open("Please fill all required fields", 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      return;
    }
    
    if (this.unAllocatedAmt != 0) {
      if (!confirm("Total Depreciation Expense Per Tax Return is not fully adjusted. Confirm to save the changes.")) {
        return;
      }
    }
    
    const finalData: any[] = [];
    
    for (let x = 0; x < this.f4562FinalData.length; x++) {
      if (this.f4562FinalData[x].ROW_TYPE == 'CH') {
        this.f4562FinalData[x].Line_data.splice(0, 2);
        if (this.f4562FinalData[x].Line_data.length != null && this.f4562FinalData[x].Line_data.length != undefined) {
          for (let j = 0; j < this.f4562FinalData[x].Line_data.length; j++) {
            for (let k = 0; k < this.f4562FinalData[x].Line_data[j].Line_data_occ.length; k++) {
              finalData.push(this.f4562FinalData[x].Line_data[j].Line_data_occ[k]);
            }
          }
        }
        if (this.f4562FinalData[x].DELETE_ROWS.length != null && this.f4562FinalData[x].DELETE_ROWS.length != undefined) {
          for (let j = 0; j < this.f4562FinalData[x].DELETE_ROWS.length; j++) {
            for (let k = 0; k < this.f4562FinalData[x].DELETE_ROWS[j].Line_data_occ.length; k++) {
              this.f4562FinalData[x].DELETE_ROWS[j].Line_data_occ[k].IS_CHANGED_FLAG = "D";
              finalData.push(this.f4562FinalData[x].DELETE_ROWS[j].Line_data_occ[k]);
            }
          }
        }
      }
      if (this.f4562FinalData[x].ROW_TYPE == 'TH' || 
          this.f4562FinalData[x].ROW_TYPE == 'DT' || 
          this.f4562FinalData[x].ROW_TYPE == 'RT') {
        for (let j = 0; j < this.f4562FinalData[x].Line_data.length; j++) {
          finalData.push(this.f4562FinalData[x].Line_data[j]);
        }
      }
      if (this.f4562FinalData[x].ROW_TYPE == 'LH') {
        for (let j = 0; j < this.f4562FinalData[x].Line_data.length; j++) {
          for (let l = 0; l < this.f4562FinalData[x].Line_data[j].Line_data.length; l++) {
            finalData.push(this.f4562FinalData[x].Line_data[j].Line_data[l]);
          }
        }
      }
    }
    
    clob_settings.push({
      sso_id: this.logged_in_user,
      screen_key: this.globalService?.globalParams?.screen_key || ''
    });
    
    this.isSaveClicked = true;
    this.crudLoading = true;
    
    this.f4562Service.saveForm4562(
      this.GENERAL_CONFIG.base_url,
      finalData,
      clob_settings,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        
        if (response.errorMessage && response.errorMessage != 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Form 4562 Details are successfully updated.', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.markForCheck();
      }
    );
  }

  reset() {
    this.loadFormData();
  }

  cancel() {
    const f4562FormJSONParseData = JSON.parse(JSON.stringify(this.f4562FormData));
    if (_.isEqual(this.f4562OriginalData, f4562FormJSONParseData)) {
      this.requestGridUpdate.emit('cancel');
    } else {
      if (this.crudLoading) {
        this._snackBar.open("The transaction is still in progress", 'Close', {
          duration: 4000,
          panelClass: ['info-snackbar']
        });
        this.requestGridUpdate.emit('cancel');
      } else if (confirm("Depreciation and Amortization Information is not save. Do you want to close Form without saving changes?")) {
        this.requestGridUpdate.emit('cancel');
      }
    }
  }

  onCheckboxChange(item: any, mainIndex: number, lineNo: string, itemIndex: number, event: any) {
    item.ATTRIB_VALUE = event.target.checked ? 'Y' : 'N';
    this.valuechanged(mainIndex, null, lineNo, itemIndex);
  }

  onCheckboxChangeColumn(column: any, mainIndex: number, occurrence: any, lineNo: string, colIndex: number, event: any) {
    column.ATTRIB_VALUE = event.target.checked ? 'Y' : 'N';
    this.valuechanged(mainIndex, occurrence, lineNo, colIndex);
  }

  onDateVariousCheckboxChange(column: any, mainIndex: number, occurrence: any, lineNo: string, colIndex: number, event: any) {
    const isChecked = event.target.checked;
    
    if (isChecked) {
      // Set to "VARIOUS" when checkbox is checked
      column.ATTRIB_VALUE = 'VARIOUS';
    } else {
      // Set to current date in YYYY-MM-DD format when checkbox is unchecked
      column.ATTRIB_VALUE = this.convertToHtmlDateFormat(new Date());
    }
    
    this.valuechanged(mainIndex, occurrence, lineNo, colIndex);
  }

  /**
   * Initialize select value based on ng-init logic from AngularJS
   * Handles initialization for CONVENTION and METHOD selects in LH row type
   * This method mimics ng-init behavior - runs during template initialization
   */
  initializeSelectValueOnInit(item: any, descriptionType: string, options: string[]): any {
    // ng-init logic: item.ATTRIB_DESCRIPTION === 'Convention' ? 
    //   item.ATTRIB_VALUE == null ? item.ATTRIB_VALUE = ctrl.convensions[0] : item.ATTRIB_VALUE = item.ATTRIB_VALUE 
    //   : item.ATTRIB_VALUE = item.ATTRIB_DESCRIPTION
    if (item.ATTRIB_DESCRIPTION === descriptionType) {
      // If ATTRIB_DESCRIPTION matches the type (Convention or Method)
      if (item.ATTRIB_VALUE == null || item.ATTRIB_VALUE === undefined || item.ATTRIB_VALUE === '') {
        // If ATTRIB_VALUE is null/undefined/empty, set to first option
        item.ATTRIB_VALUE = options[0];
      }
      // Otherwise, keep ATTRIB_VALUE as is (already set)
    } else {
      // If ATTRIB_DESCRIPTION doesn't match, set ATTRIB_VALUE to ATTRIB_DESCRIPTION
      item.ATTRIB_VALUE = item.ATTRIB_DESCRIPTION;
    }
    return ''; // Return empty string for attr binding
  }

  /**
   * Initialize all select dropdowns in the form data
   * Called after data is fetched to ensure dropdowns show correct values
   */
  private initializeSelectDropdowns(formData: any[]): void {
    if (!formData) return;

    formData.forEach(row => {
      if (row.ROW_TYPE === 'LH' && row.Line_data) {
        // Process LH row type
        row.Line_data.forEach((lineItem: any) => {
          if (lineItem.Line_data) {
            lineItem.Line_data.forEach((dataItem: any) => {
              if (dataItem.EDIT_TYPE === 'select' && dataItem.DATA_TYPE === 'string') {
                // Initialize CONVENTION select
                if (dataItem.ATTRIB_SHORT_CODE && dataItem.ATTRIB_SHORT_CODE.indexOf('CONVENTION') !== -1) {
                  this.initializeSelectValueOnInit(dataItem, 'Convention', this.convensions);
                }
                // Initialize METHOD select
                if (dataItem.ATTRIB_SHORT_CODE && dataItem.ATTRIB_SHORT_CODE.indexOf('METHOD') !== -1) {
                  this.initializeSelectValueOnInit(dataItem, 'Method', this.methods);
                }
              }
            });
          }
        });
      }
    });
  }
}

